import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { BiArrowBack, BiSave, BiPrinter } from "react-icons/bi";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";

import { uploadSupportingFile, openSupportingFile } from "./supportingFile";

import {
  getJrManifest,
  saveJrManifest,
  clearManifest,
  resetManifestSave,
  jrV2Selector,
} from "../job-request/jrV2Slice";
import { Colors } from "../../../assets/Colors";

const ACTIVITY_LABELS = {
  PORT_SERVICES: "Port Services", RENTAL: "Rental",
  WASTE_MANAGEMENT_SERVICES: "Waste Management Services",
  AREA_RENTAL: "Area Rental", MANPOWER: "Manpower", GENERAL_SERVICE: "General Service",
};

const VESSEL_FIELDS = [
  { key: "VesselName",   label: "Vessel Name", required: true },
  { key: "MasterName",   label: "Master Name" },
  { key: "OwnerVessel",  label: "Owner Vessel" },
  { key: "ShipAgent",    label: "Ship Agent" },
  { key: "Flag",         label: "Flag" },
  { key: "GrossTonnage", label: "Gross Tonnage" },
  { key: "PortRegistry", label: "Port Registry" },
  { key: "PortDepature", label: "Port Depature" },
  { key: "KindCargo",    label: "Kind of Cargo" },
  { key: "Quantity",     label: "Quantity" },
];

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm text-gray-800">{value || <span className="text-gray-400">—</span>}</p>
    </div>
  );
}

const emptyManifest = {
  VesselName: "", MasterName: "", OwnerVessel: "", ShipAgent: "", Flag: "",
  GrossTonnage: "", PortRegistry: "", PortDepature: "", Eta: "", KindCargo: "", Quantity: "",
  SupportingFile: "",
};

const OperasionalShippingManifestDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const jrID = location.state?.JrID ?? null;

  const jrV2 = useSelector(jrV2Selector);
  const header = jrV2.manifest?.Header ?? null;

  const [form, setForm] = useState(emptyManifest);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (jrID) dispatch(getJrManifest(jrID));
    return () => { dispatch(clearManifest()); };
  }, [jrID]); // eslint-disable-line

  // Isi form dari data manifest. Default: ETA = waktu sekarang, field lain = "-".
  useEffect(() => {
    if (jrV2.manifest?.Manifest) {
      const m = jrV2.manifest.Manifest;
      const next = { ...emptyManifest, ...m };
      VESSEL_FIELDS.forEach(f => {
        if (!next[f.key]) next[f.key] = "-";
      });
      if (!next.Eta) next.Eta = new Date().toISOString();
      setForm(next);
    }
  }, [jrV2.manifest]);

  // Navigate balik saat save sukses
  useEffect(() => {
    if (jrV2.isManifestSaveSuccess) {
      toast.success("Shipping manifest tersimpan — JR masuk Menunggu Activity");
      dispatch(resetManifestSave());
      navigate("/operasional-shipping-manifest");
    }
  }, [jrV2.isManifestSaveSuccess]); // eslint-disable-line

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.VesselName?.trim()) { toast.error("Vessel Name wajib diisi"); return; }

    let supportingFile = form.SupportingFile || "";
    if (selectedFile) {
      setUploading(true);
      try {
        supportingFile = await uploadSupportingFile(jrID, selectedFile);
      } catch (e) {
        setUploading(false);
        toast.error("Gagal upload file pendukung");
        return;
      }
      setUploading(false);
    }

    dispatch(saveJrManifest({ JrID: jrID, ...form, SupportingFile: supportingFile }));
  };

  if (!jrID) {
    return (
      <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">
        <div className="text-center">
          <p className="mb-2">JR tidak ditemukan</p>
          <button onClick={() => navigate("/operasional-shipping-manifest")}
            className="text-sm text-orange-500 underline">Kembali</button>
        </div>
      </div>
    );
  }

  if (jrV2.isManifestLoading || !header) {
    return <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">Memuat data…</div>;
  }

  // Group lines by activity untuk tampilan read-only
  const grouped = (header.Lines ?? []).reduce((acc, l) => {
    const k = l.ActivityName || "OTHER";
    (acc[k] = acc[k] || []).push(l);
    return acc;
  }, {});

  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">

      {/* Top bar */}
      <div className="flex items-center justify-between py-4 mb-2">
        <button onClick={() => navigate("/operasional-shipping-manifest")}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <button onClick={handleSave} disabled={jrV2.isSaving || uploading}
          className="flex items-center gap-1.5 px-5 py-1.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
          style={{ background: Colors.primary }}>
          <BiSave /> {uploading ? "Mengunggah…" : jrV2.isSaving ? "Menyimpan…" : "Simpan Manifest"}
        </button>
      </div>

      {/* Header JR */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-4">Informasi Job Request</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <InfoField label="No. JR"        value={header.JrID} />
          <InfoField label="No. QO"        value={header.QoNo} />
          <InfoField label="Nama Klien"    value={header.NamaKlien} />
          <InfoField label="No. Kontrak"   value={header.NoKontrak} />
          <InfoField label="Tgl Pekerjaan" value={header.WorkDate} />
          <InfoField label="SPK No"        value={header.SPKNo} />
          <InfoField label="WSB No"        value={header.WSBNo} />
          <InfoField label="Prioritas"     value={header.Priority} />
        </div>
      </div>

      {/* Shipping manifest form */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-4">Shipping Manifest</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {VESSEL_FIELDS.map(f => (
            <div key={f.key}>
              <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                {f.label} {f.required && <span className="text-red-500">*</span>}
              </label>
              <input
                type={f.type || "text"}
                value={form[f.key] ?? ""}
                onChange={e => update(f.key, e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-blue-400" />
            </div>
          ))}

          {/* ETA — datepicker dengan jam */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">ETA</label>
            <DatePicker
              showTimeInput
              dateFormat="dd-MM-yyyy HH:mm"
              selected={form.Eta ? new Date(form.Eta) : null}
              onChange={d => update("Eta", d ? d.toISOString() : "")}
              className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-blue-400"
              placeholderText="Pilih tanggal & jam" />
          </div>
        </div>

        {/* Change Supporting File */}
        <div className="mt-4 pt-4 border-t">
          <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
            Change Supporting File
          </label>
          {form.SupportingFile && (
            <button
              type="button"
              onClick={() => openSupportingFile(form.SupportingFile)}
              className="flex items-center gap-1.5 mb-2 text-sm text-blue-600 hover:underline">
              <BiPrinter className="text-lg" /> Lihat file pendukung saat ini
            </button>
          )}
          <input
            type="file"
            onChange={e => setSelectedFile(e.target.files?.[0] ?? null)}
            className="block text-sm border border-gray-300 rounded py-2 px-3 bg-white" />
          {selectedFile && (
            <p className="mt-1 text-xs text-gray-500">File dipilih: {selectedFile.name}</p>
          )}
        </div>
      </div>

      {/* Daftar layanan (read-only) */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-4">Layanan Job Request</h3>
        {Object.keys(grouped).length === 0 ? (
          <p className="text-sm text-gray-400">Tidak ada item layanan</p>
        ) : (
          Object.entries(grouped).map(([act, lines]) => (
            <div key={act} className="mb-4">
              <p className="font-semibold text-sm mb-1.5" style={{ color: Colors.primary }}>
                {ACTIVITY_LABELS[act] ?? act}
              </p>
              <ul className="list-disc ml-6 text-sm text-gray-700 space-y-0.5">
                {lines.map(l => (
                  <li key={l.JrLineID}>{l.ItemDisplay || l.ItemName}</li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OperasionalShippingManifestDetail;
